import type { ComponentType } from "react";
import {
  Route,
  createBrowserRouter,
  RouterProvider,
  createRoutesFromElements,
  Link,
  Navigate,
} from "react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import { DialogRoot } from "@supply25/design/dialog";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { isFeatureFlagOn } from "./service/featureFlags.ts";
import { client } from "./queryClient.ts";
import * as loaders from "@/loaders.ts";
import { FullPageLoading } from "./components/FullPageLoading.tsx";
import { RootLayout, RootBoundary, PublicLayout } from "./RootLayout.tsx";
import { WorkspaceProvider } from "@/context/WorkspaceProvider.tsx";
import type { OrganisationInfoConfig } from "./components/forms/createOrganisation/Info.tsx";
import { ArrowLeft, ArrowRight } from "@untitled-ui/icons-react";
import type { OrganisationDomainsConfig } from "./components/forms/createOrganisation/Domains.tsx";
import type { OrganisationWorkspacesConfig } from "./components/forms/createOrganisation/Workspace.tsx";
import type { OrganisationMembersConfig } from "./components/forms/createOrganisation/Members.tsx";
import { MaintenancePage } from "./components/MaintenancePage.tsx";

/** Helper to make lazy routes less verbose whilst maintaining type-safety
 * @example
 * load(() => import("./pages/example.tsx"), "ExampleComponentName")
 */
function load<
  Name extends string,
  Module extends Record<Name, ComponentType> & { handle?: unknown },
>(load: () => Promise<Module>, name: Name) {
  return async () => {
    const module = await load();
    return { Component: module[name], handle: module.handle };
  };
}

const routes = createRoutesFromElements(
  <Route HydrateFallback={FullPageLoading} ErrorBoundary={RootBoundary}>
    <Route path="/maintenance" Component={MaintenancePage} />
    {/* Public routes never check auth */}
    <Route Component={PublicLayout}>
      <Route path="oauth/callback" loader={loaders.oauthLogin} />
      <Route
        path="login"
        lazy={load(() => import("./pages/loginPage"), "LoginPage")}
      />
      <Route
        path="verify-domain/:verificationId"
        lazy={load(
          () => import("./pages/verifyDomainPage"),
          "VerifyDomainPage"
        )}
      />
      <Route
        path="applications/:packageId"
        loader={loaders.getPublicTenderPackage(client)}
        lazy={
          isFeatureFlagOn("newPackageSubmission")
            ? load(
                () => import("./pages/package/public.tsx"),
                "PublicPackageSubmission"
              )
            : load(
                () =>
                  import("./pages/public/activeTenderPackageSummary/index.tsx"),
                "PublicTenderPackageSummary"
              )
        }
      />
    </Route>

    {/* Ensures user authenticated and provided to all children via context */}
    <Route loader={loaders.getUser(client)} Component={RootLayout}>
      <Route
        path="*"
        lazy={load(() => import("./pages/notFoundPage"), "NotFoundPage")}
      />
      <Route path="/" loader={loaders.redirectToWorkspace(client)} />

      {/* Onboarding */}
      <Route
        path="welcome"
        lazy={load(
          () => import("./pages/welcomePages/WelcomeLayout.tsx"),
          "WelcomeLayout"
        )}
      >
        <Route index loader={loaders.checkAvailableOrgs(client)} />
        <Route
          path="create"
          lazy={load(
            () => import("./pages/welcomePages/CreateOrg.tsx"),
            "CreateOrg"
          )}
        />
        <Route
          path="join"
          lazy={load(
            () => import("./pages/welcomePages/JoinLayout.tsx"),
            "JoinLayout"
          )}
        >
          <Route
            index
            lazy={load(
              () => import("./pages/welcomePages/JoinOrganisationPage.tsx"),
              "JoinOrganisationPage"
            )}
          />
          <Route
            loader={loaders.listAllMyOrganisationsInfo(client)}
            path="workspaces"
            lazy={load(
              () => import("./pages/welcomePages/JoinWorkspacePage.tsx"),
              "JoinWorkspacePage"
            )}
          />
        </Route>
        <Route
          path="edit/:organisationId"
          lazy={load(
            () => import("./pages/welcomePages/create/Layout.tsx"),
            "CreateLayout"
          )}
          loader={loaders.getOrg(client)}
        >
          <Route
            index
            lazy={load(
              () => import("./components/forms/createOrganisation/Info.tsx"),
              "OrganisationInfoForm"
            )}
            handle={
              {
                heading: "Set up your organisation",
                description:
                  "This is where you can create Assessment Packages, Assessments and Questions. You can invite members to collaborate with you.",
                nextUrl: "domains",
                nav: (
                  <nav className="flex flex-row justify-between">
                    <Link to="/welcome" className="Button" theme="text">
                      <ArrowLeft />
                      Back
                    </Link>
                    <Link to="domains" className="Button" theme="primary">
                      Next <ArrowRight />
                    </Link>
                  </nav>
                ),
              } satisfies OrganisationInfoConfig
            }
          />
          <Route
            path="domains"
            lazy={load(
              () => import("./components/forms/createOrganisation/Domains.tsx"),
              "OrganisationDomainsForm"
            )}
            loader={loaders.getOrgDomains(client)}
            handle={
              {
                nav: (
                  <nav className="flex flex-row justify-between">
                    <Link to=".." className="Button" theme="text">
                      <ArrowLeft />
                      Back
                    </Link>
                    <Link to="../workspaces" className="Button" theme="primary">
                      Next
                      <ArrowRight />
                    </Link>
                  </nav>
                ),
              } satisfies OrganisationDomainsConfig
            }
          />
          <Route
            path="workspaces"
            lazy={load(
              () =>
                import("./components/forms/createOrganisation/Workspace.tsx"),
              "OrganisationWorkspaceForm"
            )}
            handle={
              {
                nav: (
                  <nav className="flex justify-between">
                    <Link className="Button" to="../domains" theme="text">
                      <ArrowLeft />
                      Back
                    </Link>
                    <Link className="Button" to="../members" theme="primary">
                      Next
                      <ArrowRight />
                    </Link>
                  </nav>
                ),
              } satisfies OrganisationWorkspacesConfig
            }
          />
          <Route
            path="members"
            lazy={load(
              () => import("./components/forms/createOrganisation/Members.tsx"),
              "OrganisationMembersForm"
            )}
            handle={
              {
                nav: (
                  <nav className="flex justify-between">
                    <Link className="Button" to="../workspaces" theme="text">
                      <ArrowLeft />
                      Back
                    </Link>
                    <Link
                      className="Button"
                      to="/welcome/join/workspaces"
                      theme="primary"
                    >
                      Finish
                      <ArrowRight />
                    </Link>
                  </nav>
                ),
              } satisfies OrganisationMembersConfig
            }
          />

          {/* Workspace settings */}
          <Route path="workspace">
            <Route
              path=":wsId"
              lazy={load(
                () => import("./pages/workspaceSettingsPage/Layout.tsx"),
                "WorkspaceSettingsLayout"
              )}
            >
              <Route
                index
                lazy={load(
                  () => import("./components/forms/editWorkspace/Info.tsx"),
                  "WorkspaceInfoForm"
                )}
              />
            </Route>
          </Route>
        </Route>
      </Route>

      {/* Organisation selection routes */}
      <Route
        path="organisation"
        index
        loader={loaders.redirectToFirstOrganisation(client)}
      />

      {/* Ensures org/workspace are loaded and provided to all children via context */}
      <Route
        path="/w/:workspaceId"
        loader={loaders.getWorkspace(client)}
        Component={WorkspaceProvider}
      >
        <Route
          path="*"
          lazy={load(() => import("./pages/notFoundPage"), "NotFoundPage")}
        />
        <Route
          path="notifications"
          loader={loaders.listAllMyNotifications(client)}
          lazy={load(
            () => import("./pages/notificationsPage/index.tsx"),
            "NotificationsPage"
          )}
        />
        <Route
          path="settings"
          lazy={load(
            () => import("./pages/settingsPage/index.tsx"),
            "SettingsPage"
          )}
        />
        <Route
          index
          loader={loaders.redirectToCorrectHome(client)}
          lazy={load(() => import("./pages/dashboard/index.tsx"), "Dashboard")}
        />
        <Route
          path="applications/:packageId"
          loader={loaders.getPrivateTenderPackage(client)}
          lazy={
            isFeatureFlagOn("newPackageSubmission")
              ? load(
                  () => import("./pages/package/submission.tsx"),
                  "PackageSubmission"
                )
              : load(
                  () => import("./pages/tenderPackageSummary/index.tsx"),
                  "TenderPackageSummary"
                )
          }
        />

        <Route path="organisation">
          <Route index loader={loaders.redirectToFirstOrganisation(client)} />
          {/* Create a new organisation */}
          <Route
            path="create/:organisationId"
            lazy={load(
              () =>
                import("./pages/organisationPage/CreateOrganisationLayout.tsx"),
              "CreateOrganisationLayout"
            )}
            loader={loaders.getOrg(client)}
          >
            <Route
              index
              lazy={load(
                () => import("./components/forms/createOrganisation/Info.tsx"),
                "OrganisationInfoForm"
              )}
              handle={
                {
                  heading: "Set up your organisation",
                  description:
                    "This is where you can create Assessment Packages, Assessments and Questions. You can invite members to collaborate with you.",
                  nextUrl: "domains",
                  nav: (
                    <nav className="flex flex-row justify-between">
                      <Link to=".." className="Button" theme="text">
                        <ArrowLeft />
                        Back
                      </Link>
                      <Link to="domains" className="Button" theme="primary">
                        Next <ArrowRight />
                      </Link>
                    </nav>
                  ),
                } satisfies OrganisationInfoConfig
              }
            />
            <Route
              path="domains"
              lazy={load(
                () =>
                  import("./components/forms/createOrganisation/Domains.tsx"),
                "OrganisationDomainsForm"
              )}
              loader={loaders.getOrgDomains(client)}
              handle={
                {
                  nav: (
                    <nav className="flex flex-row justify-between">
                      <Link to=".." className="Button" theme="text">
                        <ArrowLeft />
                        Back
                      </Link>
                      <Link
                        to="../workspaces"
                        className="Button"
                        theme="primary"
                      >
                        Next
                        <ArrowRight />
                      </Link>
                    </nav>
                  ),
                } satisfies OrganisationDomainsConfig
              }
            />
            <Route
              path="workspaces"
              lazy={load(
                () =>
                  import("./components/forms/createOrganisation/Workspace.tsx"),
                "OrganisationWorkspaceForm"
              )}
              handle={
                {
                  nav: (
                    <nav className="flex justify-between">
                      <Link className="Button" to="../domains" theme="text">
                        <ArrowLeft />
                        Back
                      </Link>
                      <Link className="Button" to="../members" theme="primary">
                        Next
                        <ArrowRight />
                      </Link>
                    </nav>
                  ),
                } satisfies OrganisationWorkspacesConfig
              }
            />
            <Route
              path="members"
              lazy={load(
                () =>
                  import("./components/forms/createOrganisation/Members.tsx"),
                "OrganisationMembersForm"
              )}
              handle={
                {
                  nav: (
                    <nav className="flex justify-between">
                      <Link className="Button" to="../workspaces" theme="text">
                        <ArrowLeft />
                        Back
                      </Link>
                      <Link className="Button" to="../../.." theme="primary">
                        Finish
                        <ArrowRight />
                      </Link>
                    </nav>
                  ),
                } satisfies OrganisationMembersConfig
              }
            />
          </Route>

          {/* Edit an existing organisation */}
          <Route
            path="edit/:organisationId"
            lazy={load(
              () =>
                import("./pages/organisationPage/CreateOrganisationLayout.tsx"),
              "CreateOrganisationLayout"
            )}
            loader={loaders.getOrg(client)}
          >
            <Route
              index
              lazy={load(
                () => import("./components/forms/createOrganisation/Info.tsx"),
                "OrganisationInfoForm"
              )}
              handle={
                {
                  heading: "Information",
                } satisfies OrganisationInfoConfig
              }
            />
            <Route
              path="domains"
              lazy={load(
                () =>
                  import("./components/forms/createOrganisation/Domains.tsx"),
                "OrganisationDomainsForm"
              )}
              loader={loaders.getOrgDomains(client)}
            />
            <Route
              path="workspaces"
              lazy={load(
                () =>
                  import("./components/forms/createOrganisation/Workspace.tsx"),
                "OrganisationWorkspaceForm"
              )}
            />
            <Route
              path="members"
              lazy={load(
                () =>
                  import("./components/forms/createOrganisation/Members.tsx"),
                "OrganisationMembersForm"
              )}
            />
          </Route>

          {/* Workspace settings */}
          <Route path="edit/:organisationId/workspace">
            <Route
              path=":wsId"
              lazy={load(
                () => import("./pages/workspaceSettingsPage/Layout.tsx"),
                "WorkspaceSettingsLayout"
              )}
            >
              <Route
                index
                lazy={load(
                  () => import("./components/forms/editWorkspace/Info.tsx"),
                  "WorkspaceInfoForm"
                )}
              />
              <Route
                path="members"
                lazy={load(
                  () => import("./components/forms/editWorkspace/Members.tsx"),
                  "WorkspaceMembersForm"
                )}
              />
              <Route
                path="features"
                loader={loaders.ensureCanManageFeatures(client)}
                lazy={load(
                  () => import("./components/forms/editWorkspace/Features.tsx"),
                  "FeaturesSettings"
                )}
              />
            </Route>
          </Route>

          {/* Organisation settings */}
          <Route
            path=":organisationId"
            loader={loaders.ensureOrgInfo(client)}
            lazy={load(
              () => import("./pages/organisationPage"),
              "OrganisationLayout"
            )}
          >
            <Route
              index
              lazy={load(
                () => import("./pages/organisationPage/WorkspacesView.tsx"),
                "WorkspacesView"
              )}
            />
            <Route
              path="join/invite"
              lazy={load(
                () => import("./pages/organisationPage/JoinInvite.tsx"),
                "JoinInvite"
              )}
            />
            <Route
              path="join/domain"
              lazy={load(
                () => import("./pages/organisationPage/JoinDomain.tsx"),
                "JoinDomain"
              )}
            />

            <Route path="members">
              <Route
                loader={loaders.getAllOrgUsers(client, true)}
                index
                lazy={load(
                  () => import("./pages/organisationPage/UserView.tsx"),
                  "UserView"
                )}
              />
            </Route>
            <Route
              path="domains"
              loader={loaders.getOrganisationDomains(client)}
              lazy={load(
                () => import("./pages/organisationPage/DomainsView.tsx"),
                "DomainsView"
              )}
            />
            <Route
              path="info"
              loader={loaders.getOrganisationSettings(client)}
              lazy={load(
                () => import("./pages/organisationPage/ProfileView.tsx"),
                "ProfileView"
              )}
            />
            <Route
              path="requests"
              lazy={load(
                () => import("./pages/organisationPage/RequestView.tsx"),
                "RequestView"
              )}
              loader={loaders.getAllWorkspaceJoinRequests(client)}
            />
          </Route>
        </Route>

        <Route
          path="applications"
          lazy={load(
            () => import("./pages/mySubmissionsPage"),
            "MySubmissionsPage"
          )}
          loader={loaders.listMySubmissions(client)}
        />
        <Route
          path="faq"
          lazy={load(() => import("./pages/faqPage"), "FaqPage")}
        />

        {isFeatureFlagOn("createAssessment") && (
          <Route path="assessments">
            <Route
              index
              loader={loaders.listCollections(client)}
              lazy={load(
                () => import("./pages/allAssessmentsPage"),
                "AllAssessmentsPage"
              )}
            />
            <Route
              path="create"
              lazy={load(
                () => import("./pages/assessmentBuilder/Create.tsx"),
                "AssessmentCreate"
              )}
            />
            <Route
              path=":collectionId/two-stage-builder"
              loader={loaders.getCollection(client)}
              lazy={load(
                () =>
                  import("./pages/twoStageAssessmentBuilder/TwoStageAssessmentBuilder.tsx"),
                "TwoStageAssessmentBuilder"
              )}
            >
              <Route index element={<Navigate replace to="information" />} />
              <Route
                path="information"
                lazy={load(
                  () =>
                    import("./pages/twoStageAssessmentBuilder/TwoStageAssessmentBuilderInformation.tsx"),
                  "TwoStageAssessmentBuilderInformation"
                )}
              />
              <Route
                path="stage-1"
                lazy={load(
                  () =>
                    import("./pages/twoStageAssessmentBuilder/TwoStageAssessmentBuilderStageOne.tsx"),
                  "TwoStageAssessmentBuilderStageOne"
                )}
              />
              <Route
                path="stage-2"
                lazy={load(
                  () =>
                    import("./pages/twoStageAssessmentBuilder/TwoStageAssessmentBuilderStageTwo.tsx"),
                  "TwoStageAssessmentBuilderStageTwo"
                )}
              />
              <Route
                path="summary"
                lazy={load(
                  () =>
                    import("./pages/twoStageAssessmentBuilder/TwoStageAssessmentBuilderSummary.tsx"),
                  "TwoStageAssessmentBuilderSummary"
                )}
              />
            </Route>
            <Route
              path=":collectionId/overview"
              loader={loaders.listCollectionVersions(client)}
              lazy={load(
                () =>
                  import("./pages/assessments/AssessmentsOverviewLayout.tsx"),
                "AssessmentsOverviewLayout"
              )}
            >
              <Route
                index
                lazy={load(
                  () => import("./pages/assessments/AssessmentsOverview.tsx"),
                  "AssessmentsOverview"
                )}
              />
              <Route
                path="history"
                lazy={load(
                  () => import("./pages/assessments/VersionHistory.tsx"),
                  "VersionHistory"
                )}
              />
            </Route>
            <Route path=":collectionId" loader={loaders.getCollection(client)}>
              <Route
                path="a/:assessmentId"
                loader={loaders.getAssessment(client)}
                lazy={load(
                  () => import("./pages/assessmentBuilder/Layout.tsx"),
                  "AssessmentBuilderLayout"
                )}
              >
                <Route
                  loader={loaders.getCollection(client)}
                  path="detail"
                  lazy={load(
                    () =>
                      import("./pages/assessmentBuilder/CollectionDetail.tsx"),
                    "CollectionDetail"
                  )}
                />
                <Route
                  path="introduction"
                  lazy={load(
                    () =>
                      import("./pages/assessmentBuilder/IntroductionEditor.tsx"),
                    "IntroductionEditor"
                  )}
                />
                <Route
                  loader={loaders.getAssessmentWithSection(client)}
                  path="s/:sectionId/question"
                  lazy={load(
                    () =>
                      import("./pages/assessmentBuilder/AddQuestionView.tsx"),
                    "AddQuestionView"
                  )}
                />
                <Route
                  loader={loaders.getAssessmentWithQuestion(client)}
                  path="s/:sectionId/question/:questionId"
                  lazy={load(
                    () =>
                      import("./pages/assessmentBuilder/QuestionEditor.tsx"),
                    "QuestionEditor"
                  )}
                />
                <Route
                  path="outcomes"
                  lazy={load(
                    () => import("./pages/assessmentBuilder/OutcomeEditor.tsx"),
                    "OutcomeEditor"
                  )}
                />
              </Route>
            </Route>
          </Route>
        )}

        {isFeatureFlagOn("riskMap") && (
          <Route
            lazy={load(
              () => import("./pages/riskMap/index.tsx"),
              "RiskMapPage"
            )}
            path="risk-map"
            loader={loaders.getSupplyChain(client)}
          />
        )}

        {isFeatureFlagOn("contractV2") && (
          <Route path="contracts">
            <Route
              index
              lazy={load(
                () => import("./pages/contracts/List.tsx"),
                "ContractListPage"
              )}
              loader={loaders.listContracts(client)}
            />
            <Route path=":contractId" loader={loaders.getContract(client)}>
              <Route
                lazy={load(
                  () => import("./pages/contracts/v2/Layout.tsx"),
                  "ContractLayoutV2"
                )}
              >
                <Route
                  index
                  lazy={load(
                    () => import("./pages/contracts/v2/overview/Overview.tsx"),
                    "ContractOverviewV2"
                  )}
                />
                <Route
                  path="reassessments"
                  lazy={load(
                    () =>
                      import("./pages/contracts/v2/reassessments/Reassessments.tsx"),
                    "ContractReassessmentsV2"
                  )}
                />
                <Route
                  path="suppliers"
                  loader={loaders.getContractMembers(client)}
                  lazy={load(
                    () => import("./pages/contracts/v2/Suppliers.tsx"),
                    "ContractSuppliersV2"
                  )}
                />
              </Route>
              <Route
                path="reassessments/schedule"
                loader={loaders.getContractAssessments(client)}
                lazy={load(
                  () => import("./pages/contracts/v2/ScheduleReassessment.tsx"),
                  "ScheduleReassessmentV2"
                )}
              />
              <Route
                path="reassessments/result/:urlId"
                lazy={load(
                  () => import("./pages/contracts/v2/ReassessmentResult.tsx"),
                  "ReassessmentResultV2"
                )}
              />
            </Route>
          </Route>
        )}

        <Route path="packages" loader={loaders.listPackages(client)}>
          <Route
            index
            lazy={load(
              () => import("./pages/packages/List.tsx"),
              "ListPackages"
            )}
          />
          <Route
            path="create/:id"
            loader={loaders.getPackage(client)}
            lazy={load(
              () => import("./pages/packages/create/Layout.tsx"),
              "CreatePackageLayout"
            )}
          >
            <Route
              index
              lazy={load(
                () => import("./pages/packages/create/Summary.tsx"),
                "CreatePackageSummary"
              )}
            />
            <Route
              path="settings"
              lazy={load(
                () => import("./pages/packages/create/Settings.tsx"),
                "CreatePackageSettings"
              )}
            />
            <Route
              path="assessments"
              lazy={load(
                () => import("./pages/packages/create/Assessments.tsx"),
                "CreatePackageAssessments"
              )}
            />
            <Route
              path="setup"
              lazy={load(
                () => import("./pages/packages/create/Setup.tsx"),
                "CreatePackageSetup"
              )}
            />
          </Route>
          <Route path=":id" loader={loaders.getPackageDetails(client)}>
            <Route
              lazy={load(
                () => import("./pages/packages/Layout.tsx"),
                "PackageLayout"
              )}
            >
              <Route
                index
                loader={loaders.getPackageMetrics(client)}
                lazy={load(
                  () => import("./pages/packages/Details.tsx"),
                  "PackageDetails"
                )}
              />
              <Route
                path="settings"
                lazy={load(
                  () => import("./pages/packages/Settings.tsx"),
                  "PackageSettings"
                )}
              />
              <Route
                path="submissions"
                lazy={load(
                  () => import("./pages/packages/Submissions.tsx"),
                  "PackageSubmissions"
                )}
              />
            </Route>
            <Route path="submissions/:supplierId">
              <Route
                lazy={load(
                  () => import("./pages/packages/results/Layout.tsx"),
                  "PackageResultsLayout"
                )}
              >
                <Route
                  index
                  lazy={load(
                    () => import("./pages/packages/results/Plans.tsx"),
                    "PackageResultsPlans"
                  )}
                />
                <Route
                  path="all"
                  lazy={load(
                    () => import("./pages/packages/results/All.tsx"),
                    "PackageResultsAll"
                  )}
                />
              </Route>
              <Route
                path=":questionId"
                lazy={load(
                  () => import("./pages/packages/results/Review.tsx"),
                  "PackageResultsReview"
                )}
              />
            </Route>
          </Route>
        </Route>
      </Route>
    </Route>
  </Route>
);

const router = createBrowserRouter(routes);

export const App = () => {
  return (
    <QueryClientProvider client={client}>
      <RouterProvider router={router} />
      <DialogRoot />
      <ReactQueryDevtools />
    </QueryClientProvider>
  );
};
